import CarDetails from '@common/pages/Dashboard/components/CarDetails';
import CarDetailSkeleton from '@common/pages/Dashboard/components/CarDetailsSkeleton';
import { autoBidSchema } from '@common/schemas/autoBidSchema';
import { auctionTypes } from '@common/utils/constants/auction';
import { deliveryModes } from '@common/utils/constants/deliveryModes';
import { parsedVehicle } from '@common/utils/methods/parsedVehicleForCarData';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Radio,
  styled,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import addToListLogo from '../../../../components/assets/add-to-list.svg';
import auctionLogo from '../../../../components/assets/auction.svg';
import PriceInput from '../../../../components/custom/CustomInputs/PriceInput';
import CustomCheckbox from '../../../../components/ui/CustomCheckbox';
import FieldErrorText from '../../../../components/ui/FieldErrorText';
import theme from '../../../../theme';
import { editAlertDialog, setAlertDialog } from '../../../../toolkit/alertDialog/alertDialogSlice';
import { useGetAgentDetailsQuery, useLazyGetAgentDetailsQuery } from '../../../../toolkit/carDetails/carDetailsApi';
import {
  useLazyAddtoWatchListQuery,
  useLazyGetAutoBidHistoryQuery,
  useSetAutoBidBuyNowMutation,
  useSetAutoBidMutation,
} from '../../../../toolkit/upcomingCars/upcomingCarsApi';
import { useLazyGetVehicleQuery } from '../../../../toolkit/vehicle/vehicleApi';
import { Bidding_Through } from '../../../../utils/constants/auction';
import { routes } from '../../../../utils/constants/routes';
import SaleInformation from '../SaleInformation';
import { mainContainer } from '../VehicleInfoSidebarSx';
import { sidebarBox, sidebarButton, topButtonContainer } from './UpcomingCarsDetailSx';
import CostBreakdown from "@common/components/custom/CostBreakdownSection/CostBreakdownSection";
import { USER_ROLES } from 'src/utils/constants/users/userRoles';

// You can create a custom styled Radio component for more granular control
const CustomRadio = styled(Radio)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    // Styles for the unchecked state (the outer circle)
    fontSize: '1.2rem', // Adjust size as needed
    color: 'transparent', // Make the default SVG icon transparent
    border: `2px solid ${theme.palette.grey[500]}`, // Visible border when not selected
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out', // Smooth transition for visual changes
    boxSizing: 'border-box', // Include padding and border in the element's total width and height
    '&:hover': {
      borderColor: theme.palette.primary.main, // Highlight border on hover
    },
  },
  '&.Mui-checked .MuiSvgIcon-root': {
    // Styles for the checked state (the filled center)
    // Here we make the outer border disappear and use a filled circle icon
    borderColor: theme.palette.primary.main, // Border color when checked
    backgroundColor: theme.palette.primary.main, // Fill the background with primary color
    '&::before': {
      content: '""',
      display: 'block',
      width: '60%', // Size of the inner filled circle
      height: '60%',
      borderRadius: '50%',
      backgroundColor: theme.palette.common.white, // Color of the inner dot
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
  // If you want a small dot even when not selected, you can add it here as well
  // For this request, we are just showing the border.
}));

// Alternatively, define custom icons for `Radio` to get exact control over appearance
const UncheckedRadioIcon = styled('span')(({ theme }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  border: `2px solid ${theme.palette.grey[500]}`, // Border when not selected
  backgroundColor: theme.palette.background.paper,
  boxSizing: 'border-box',
  transition: 'border 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const CheckedRadioIcon = styled('span')(({ theme }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `2px solid ${theme.palette.primary.main}`, // Border when selected (can be same as fill color)
  backgroundColor: theme.palette.primary.main, // Fill the background
  boxSizing: 'border-box',
  transition: 'border 0.2s ease-in-out, background-color 0.2s ease-in-out',
  '&:before': {
    content: '""',
    display: 'block',
    width: 8, // Size of the inner white dot
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
  },
}));

// Main Car Detail Page component
const UpcomingCarsDetail = ({
  setDetailView,
  // selectedCar = {},
}) => {
  // static data start...
  const autoBidInitialValues = React.useMemo(() => {
    return {
      start_bid_amount: '',
      maximum_bid_amount: '',
      delivery_mode: deliveryModes.Pickup,
    };
  }, []);
  // static data end...

  // useLocation...
  const location = useLocation();
  // useLocation...

  const { t } = useTranslation('upcomingCars');

  // const Vehicle_Id = location?.pathname?.split?.(`${routes.upcomingCars}/`)?.[1];
  const Vehicle_Id = location?.pathname?.split?.(`${routes.upcomingCars}/`)?.[1]?.split?.(`/`)?.[1];

  const liveAuctionOrBuyNow = location?.pathname?.includes(routes.liveAuction)
    ? auctionTypes.liveAuction
    : auctionTypes.buyNow;

  const isMobile = useMediaQuery('(max-width:1300px)');

  // useDispatch...
  const dispatch = useDispatch();
  // useDispatch...

  // useState variables start...
  const [selectedCar, setSelectedCar] = React.useState(null);
  const [autoBidApi, setAutoBidApi] = React.useState({
    api: null,
    dummyChange: false,
  });
  const [showBuyNowOrLiveAuctionModal, setShowBuyNowOrLiveAuctionModal] = React.useState(null);
  const [isStartAndMaximumBidSetOnLoad, setIsStartAndMaximumBidSetOnLoad] = React.useState(false);
  // useState variables end...

  // custom hooks start...
  const { data: agentDetails, isFetching } = useGetAgentDetailsQuery({
    vehicleId: selectedCar?.Vehicle_Id,
  });
  const [setAutoBidApiHandler, setAutoBidApiData] = useSetAutoBidMutation();
  const [setAutoBidBuyNowApiHandler, setAutoBidBuyNowApiData] = useSetAutoBidBuyNowMutation();
  const [getVehicleApiHandler, getVehicleApiData] = useLazyGetVehicleQuery();
  const [getAgentDetailsApiHandler, getAgentDetailsApiData] = useLazyGetAgentDetailsQuery();
  const [addToWatchListHandler, addToWatchListData] = useLazyAddtoWatchListQuery();
  const [getAutoBidHistoryApiHandler, getAutoBidHistoryApiData] = useLazyGetAutoBidHistoryQuery();
  // custom hooks end...

  // auto bid form schema start...

  const autoBidFormik = useFormik({
    initialValues: autoBidInitialValues,
    validationSchema: autoBidSchema({
      currentStartingBid: parseInt(getAutoBidHistoryApiData?.data?.auto_bids?.[0]?.Start_Bid_Amt || 0),
      currentMaximumBid: parseInt(getAutoBidHistoryApiData?.data?.auto_bids?.[0]?.Max_Bid_Amt || 0),
      t
    }),
    onSubmit: async (values) => {
      const formData = new FormData();

      const payload = {
        ...values,
      };

      for (let i = 0; i < Object.entries(payload).length; i++) {
        formData.append(Object.entries(payload)?.[i]?.[0], Object.entries(payload)?.[i]?.[1]);
      }

      formData.append(`vehicle_id`, selectedCar?.Vehicle_Id);

      let res;
      res =
        autoBidApi?.api === Bidding_Through.Auction
          ? await setAutoBidApiHandler({
              payload: formData,
            })
          : await setAutoBidBuyNowApiHandler({
              payload: formData,
            });

      if (res?.error) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
          })
        );
      } else if (('result' in res?.data && res?.data?.result) || ('status' in res?.data && res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Success!`,
            message: `Auto bid set successfully!`,
          })
        );
        autoBidFormik.resetForm();
      }
    },
  });

  const onSubmitHandler = async (auctionType) => {
    let autoBidApiHandler;

    if (auctionType === Bidding_Through.BuyNow) {
      autoBidApiHandler = setAutoBidBuyNowApiHandler;
    } else {
      autoBidApiHandler = setAutoBidApiHandler;
    }

    const formData = new FormData();

    const payload = {
      ...autoBidFormik?.values,
    };

    for (let i = 0; i < Object.entries(payload).length; i++) {
      formData.append(Object.entries(payload)?.[i]?.[0], Object.entries(payload)?.[i]?.[1]);
    }

    formData.append(`vehicle_id`, selectedCar?.Vehicle_Id);

    const res = await autoBidApiHandler({
      payload: formData,
    });

    if (res?.error) {
      dispatch(
        setAlertDialog({
          open: true,
          title: `Error!`,
          message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
        })
      );
    } else if (res?.data) {
      dispatch(
        setAlertDialog({
          open: true,
          title: `Success!`,
          message: `Auto bid set successfully!`,
        })
      );
    }
  };
  // auto bid form schema end...

  // Handlers start...
  // const parsedVehicle = (selectedCar) => {
  //   const images = [];
  //   const carImages = getImagesHandler({
  //     data: {
  //       vehicle: selectedCar
  //     }
  //   });

  //   for (let i = 0; i < Object.entries(carImages?.images)?.length; i++) {
  //     for (let j = 0; j < Object.entries(carImages?.images)?.[i]?.[1]?.length; j++) {
  //       if (Object.entries(carImages?.images)?.[i]?.[1]?.[j]) {
  //         images.push(Object.entries(carImages?.images)?.[i]?.[1]?.[j])
  //       }
  //     }
  //   }

  //   return {
  //     ...selectedCar,
  //     images
  //   };
  // }
  // Handlers end...

  // useEffect start...
  React.useEffect(() => {
    if (Vehicle_Id) {
      (async () => {
        const res = await getVehicleApiHandler({
          Vehicle_Id,
        });

        if (res?.error) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error`,
              message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
            })
          );
        } else {
          setSelectedCar(res?.data?.vehicle);
        }
      })();
    }
  }, [Vehicle_Id]);

  React.useEffect(() => {
    if (Vehicle_Id) {
      (async () => {
        const res = await getAgentDetailsApiHandler({
          vehicleId: Vehicle_Id,
        });

        if (res?.error) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error`,
              message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
            })
          );
        }
      })();
    }
  }, [Vehicle_Id]);

  React.useEffect(() => {
    if (autoBidApi?.api) {
      autoBidFormik.handleSubmit();
    }
  }, [autoBidApi?.dummyChange]);

  React.useEffect(() => {
    if (!Vehicle_Id) {
      return;
    }

    (async () => {
      await getAutoBidHistoryApiHandler({
        Vehicle_Id,
      });
    })();
  }, [Vehicle_Id, setAutoBidApiData?.isSuccess, setAutoBidBuyNowApiData?.isSuccess]);

  React.useEffect(() => {
    if (
      'status' in (getAutoBidHistoryApiData?.data || {}) &&
      getAutoBidHistoryApiData?.data?.status &&
      getAutoBidHistoryApiData?.data?.auto_bids?.[0]?.Start_Bid_Amt &&
      getAutoBidHistoryApiData?.data?.auto_bids?.[0]?.Max_Bid_Amt &&
      !isStartAndMaximumBidSetOnLoad
    ) {
      autoBidFormik.setValues((values) => {
        return {
          ...values,
          start_bid_amount: parseInt(getAutoBidHistoryApiData?.data?.auto_bids?.[0]?.Start_Bid_Amt),
          maximum_bid_amount: parseInt(getAutoBidHistoryApiData?.data?.auto_bids?.[0]?.Max_Bid_Amt),
        };
      });

      setIsStartAndMaximumBidSetOnLoad(true);
    }
  }, [getAutoBidHistoryApiData?.data]);
  // useEffect end...

  return (
    <Grid container spacing={0} sx={mainContainer}>
      {getVehicleApiData?.isFetching || !selectedCar ? (
        //skeleton UI
        <CarDetailSkeleton />
      ) : (
        <>
          {/* Left sidebar with buttons */}
          <Grid size={{ xl: 2.8 - 0.2, lg: 4 - 0.2, md: 12, sm: 12, xs: 12 }}>
            <Box sx={sidebarBox}>
              <Box sx={topButtonContainer}>
                {/* Add To Calendar Button */}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<img src={addToListLogo} alt="Add to Calendar" />} // Assuming addToListLogo is for calendar or a similar icon
                  sx={sidebarButton}
                >
                  Add To Calendar
                </Button>
                {/* Add To Watch List Button */}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<img src={auctionLogo} alt="Add to Watch List" />} // Assuming auctionLogo is for watch list or a similar icon
                  sx={sidebarButton}
                  onClick={() => {
                    dispatch(
                      setAlertDialog({
                        open: true,
                        title: `Alert!`,
                        message: `Are you sure you want to add this car to watch list?`,
                        avoidCloseOnOK: true,
                        onOk: async () => {
                          dispatch(
                            editAlertDialog({
                              isLoading: true,
                            })
                          );

                          const res = await addToWatchListHandler({
                            Vehicle_Id,
                          });

                          if (res?.isError || ('status' in res?.data && !res?.data?.status)) {
                            dispatch(
                              editAlertDialog({
                                title: `Error!`,
                                message:
                                  res?.error?.data?.error ||
                                  res?.error?.data?.message ||
                                  res?.data?.message ||
                                  `Some error occured!`,
                                avoidCloseOnOK: false,
                                isLoading: false,
                                onOk: () => {},
                              })
                            );
                          } else {
                            dispatch(
                              editAlertDialog({
                                title: `Success!`,
                                message: `Added to watch list succesfully!`,
                                avoidCloseOnOK: false,
                                isLoading: false,
                                onOk: () => {},
                              })
                            );
                          }
                        },
                      })
                    );
                  }}
                >
                  Add To Watch List
                </Button>
              </Box>

              {/* Auto Bid Section */}
              {liveAuctionOrBuyNow === auctionTypes.liveAuction ? (
                <Box sx={{ padding: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" sx={{ marginBottom: 2 }}>
                    {t('Auto Bid')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      // alignItems: "center"
                    }}
                  >
                    <Box
                      item
                      sx={{
                        width: '47%',
                      }}
                    >
                      <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('Starting Bid')}
                        <Box
                          component="span"
                          sx={{
                            marginLeft: 1,
                            border: '1px solid #ccc',
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '0.75rem',
                          }}
                        >
                          i
                        </Box>
                      </Typography>
                      {/* <TextField
                      name={"start_bid_amount"}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="$ 3,000"
                      value={autoBidFormik?.values?.start_bid_amount}
                      onChange={autoBidFormik?.handleChange}
                    /> */}
                      <PriceInput
                        name={'start_bid_amount'}
                        value={autoBidFormik?.values?.start_bid_amount}
                        onChange={autoBidFormik?.handleChange}
                        onBlur={autoBidFormik.handleBlur}
                        // error={autoBidFormik?.errors?.start_bid_amount}
                        // error={
                        //   autoBidFormik.touched.start_bid_amount && Boolean(autoBidFormik.errors.start_bid_amount)
                        // }
                        // helperText={autoBidFormik.touched.start_bid_amount && autoBidFormik.errors.start_bid_amount}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography>$</Typography>
                            </InputAdornment>
                          ),
                        }}
                        variant="outlined"
                        size="small"
                        // sx={{
                        //   padding: "0 !important"
                        // }}
                      />

                      {autoBidFormik?.touched?.start_bid_amount && autoBidFormik?.errors?.start_bid_amount ? (
                        <FieldErrorText>{autoBidFormik?.errors?.start_bid_amount}</FieldErrorText>
                      ) : null}
                    </Box>
                    <Box
                      item
                      sx={{
                        width: '47%',
                      }}
                    >
                      <Typography>{t('Maximum Bid')}</Typography>
                      <PriceInput
                        name={'maximum_bid_amount'}
                        value={autoBidFormik?.values?.maximum_bid_amount}
                        onChange={autoBidFormik?.handleChange}
                        onBlur={autoBidFormik.handleBlur}
                        // error={autoBidFormik?.errors?.maximum_bid_amount}
                        // error={
                        //   autoBidFormik.touched.maximum_bid_amount && Boolean(autoBidFormik.errors.maximum_bid_amount)
                        // }
                        // helperText={autoBidFormik.touched.maximum_bid_amount && autoBidFormik.errors.maximum_bid_amount}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography>$</Typography>
                            </InputAdornment>
                          ),
                        }}
                        variant="outlined"
                        size="small"
                        // sx={{
                        //   padding: "0 !important"
                        // }}
                      />

                      {autoBidFormik?.touched?.maximum_bid_amount && autoBidFormik?.errors?.maximum_bid_amount ? (
                        <FieldErrorText>{autoBidFormik?.errors?.maximum_bid_amount}</FieldErrorText>
                      ) : null}
                      {/* <TextField
                      name={"maximum_bid_amount"}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="$ 4,000"
                      value={autoBidFormik?.values?.maximum_bid_amount}
                      onChange={autoBidFormik?.handleChange}
                    /> */}
                    </Box>
                  </Box>

                  {/* Delivery Mode Section */}
                  <Box sx={{ padding: 2 }}>
                    {/* <Typography sx={{ marginBottom: 1 }}>Delivery Mode</Typography> */}
                    {/* <Box
                      // component="fieldset"
                      sx={{
                        width: '100%',
                        // backgroundColor: theme.palette.background.default,
                        borderRadius: '0.75rem',
                        // padding: "1rem",
                        marginBottom: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography fontWeight={700} color={theme.palette.text.primary} sx={{ marginBottom: '0.5rem' }}>
                        Delivery Mode
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                        }}
                      >
                        <CustomCheckbox
                          label="Pickup"
                          selected={autoBidFormik?.values?.delivery_mode === deliveryModes?.Pickup}
                          onClick={() => {
                            autoBidFormik?.setValues((values) => {
                              return {
                                ...values,
                                delivery_mode: deliveryModes?.Pickup,
                              };
                            });
                          }}
                        />
                        <CustomCheckbox
                          label="Drop Off"
                          selected={autoBidFormik?.values?.delivery_mode === deliveryModes?.Dropoff}
                          onClick={() => {
                            autoBidFormik?.setValues((values) => {
                              return {
                                ...values,
                                delivery_mode: deliveryModes?.Dropoff,
                              };
                            });
                          }}
                        />
                      </Box>
                    </Box> */}
                    <Typography
                      sx={{
                        marginTop: 1,
                        fontSize: '0.9rem',
                      }}
                    >
                      20-4544 Dufferin St, North York M3H 5X2
                    </Typography>
                  </Box>

                  {/* Save Button */}
                  <Box sx={{ padding: 2 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        if (liveAuctionOrBuyNow === auctionTypes.buyNow) {
                          setAutoBidApi((autoBidApi) => {
                            return {
                              ...autoBidApi,
                              api: Bidding_Through.BuyNow,
                              dummyChange: !autoBidApi?.dummyChange,
                            };
                          });
                        } else {
                          setAutoBidApi((autoBidApi) => {
                            return {
                              ...autoBidApi,
                              api: Bidding_Through.Auction,
                              dummyChange: !autoBidApi?.dummyChange,
                            };
                          });
                        }
                      }}
                      sx={{
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#1565c0' },
                      }}
                    >
                      {setAutoBidApiData?.isLoading ? (
                        <CircularProgress
                          sx={{
                            color: theme.palette.background.paper,
                          }}
                        />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>

                  <CostBreakdown
                    ownerRole={selectedCar?.owner_type?.toLowerCase?.()?.includes?.(USER_ROLES.seller) ? 
                      USER_ROLES.seller :
                      USER_ROLES.dealer
                    }
                  />
                </Box>
              ) : null}

              {/* Sale Information (from your original code) */}
              <SaleInformation
                data={{
                  ...(getAgentDetailsApiData?.data?.[0] || {}),
                  address: selectedCar?.Location,
                  date: selectedCar?.Live_Auction_DateTime,
                }}
                car={selectedCar}
              />
            </Box>
          </Grid>
          <Divider orientation={'vertical'} flexItem />
          {/* Main content area */}
          <Grid size={{ xl: 9.2 - 0.2, lg: 8 - 0.2, md: 12, sm: 12, xs: 12 }}>
            <CarDetails car={parsedVehicle(selectedCar)} />
          </Grid>
        </>
      )}

      {/* {showBuyNowOrLiveAuctionModal ? <Modal
        open={true}
        onClose={() => {
          setShowBuyNowOrLiveAuctionModal(false);
          setAutoBidApi(autoBidApi => {
            return {
              ...autoBidApi,
              api: null,
              dummyChange: false
            }
          }); 
        }}
      >
        <Choose2Options
          open={true}
          onClose={() => {
            setShowBuyNowOrLiveAuctionModal(false);
            setAutoBidApi((autoBidApi) => {
              return {
                ...autoBidApi,
                api: null,
                dummyChange: false,
              };
            });
          }}
        />
      </Modal> : null} */}
    </Grid>
  );
};

export default UpcomingCarsDetail;
